import { Typography } from '@packages/ui/shared';
import { TableCell, TableHead, TableHeader, TableRenderer, TableRow } from '@web/components/table';
import { useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { useFormContext } from '../battery-context';
import { useAuthState } from '@packages/contexts/auth';
import { useTranslation } from 'react-i18next';

type StepFiveContentProps = {
    currentStep: number;
    displayQrCodeScanner: boolean;
};

const useStyles = createUseStyles({
    container: {
        minHeight: '82vh',
    },
    outerTable: {
        height: '100%',
        minHeight: 'auto',
    },
    actionIcons: {
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        padding: '10px',
    },
    gap16: {
        gap: '16px',
    },
    subHeaderContainer: {
        padding: '16px 24px',
        '@media(max-width: 1280px)': {
            alignItems: 'start!important',
            flexWrap: 'wrap',
        },
    },
    subHeaderLeft: {
        '@media(max-width: 1280px)': {
            flexBasis: '100%',
            justifyContent: 'start!important',
            order: 1,
        },
        '@media(max-width: 890px)': {
            order: 0,
        },
    },
    subHeaderRight: {
        '@media(max-width: 890px)': {
            flexBasis: '100%',
            justifyContent: 'start!important',
            marginLeft: 0,
        },
    },
    searchBar: {
        '@media (min-width: 1440px)': {
            width: 340,
        },
        '@media(max-width: 890px)': {
            flexBasis: '80%',
        },
    },
    tableHeader: {
        fontWeight: '500',
    },

    // new: full-width control styling for table cells
    fullWidthInput: {
        width: '100%',
    },
});

const styles = StyleSheet.create({
    tableHeader: {
        fontWeight: '500',
    },
});

const HEADER_TITLES = [
    { title: 'Part number', width: '33%' },
    { title: 'Production date', width: '33%' },
    { title: 'Serial number', width: '33%' },
];

const ProcessBatteryUpdateRowDetailsHeader: React.FC = () => {
    return <></>;
};

const BinInfoHeader: React.FC = () => {
    return (
        <>
            <TableRow>
                {HEADER_TITLES.map((header) => (
                    <TableHeader key={header.title} width={header.width}>
                        <Typography style={styles.tableHeader}>{header.title}</Typography>
                    </TableHeader>
                ))}
            </TableRow>
        </>
    );
};

const BinInfoSingleHeader: React.FC = () => {
    return (
        <>
            <TableRow>
                <TableHeader key={'Scanned QR Code'} width={'100%'}>
                    <Typography style={styles.tableHeader}>{'Scanned QR Code'}</Typography>
                </TableHeader>
            </TableRow>
        </>
    );
};

const StepFiveContent: React.FC<StepFiveContentProps> = ({ currentStep, displayQrCodeScanner }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [displayThisStep, setDisplayThisStep] = useState(false);
    const [processedFailCodes, setProcessedFailCodes] = useState<any[]>([]);
    const { data, parseBatteryCode, updateData } = useFormContext();
    const { account } = useAuthState();
    const [editingFirstPartNumber, setEditingFirstPartNumber] = useState(false);
    const [editingSecondPartNumber, setEditingSecondPartNumber] = useState(false);
    const [invalidPartNumber1, setInvalidPartNumber1] = useState(false);
    const [invalidPartNumber2, setInvalidPartNumber2] = useState(false);

    // Prefer parsed values from context (computed earlier); fallback to on-the-fly parse
    const newBinData = data.parsedNewBinData ?? parseBatteryCode(data.newBinQrCode);
    const oldBinData = data.parsedOldBinData ?? parseBatteryCode(data.oldBinQrCode);

    useEffect(() => {
        setDisplayThisStep(currentStep === 5 && !displayQrCodeScanner);
    }, [currentStep, displayQrCodeScanner]);

    if (!displayThisStep) return null;

    return (
        <Modal.Body>
            <div className="mb-2">
                <Typography variant="label">Battery Update Details</Typography>
            </div>

            <div className="row mb-2">
                <div className="col-12">
                    <TableRenderer
                        tableRowsData={[
                            {
                                rowId: '1',
                                retailer: data.selectedRetailer?.[0]?.retailerName,
                                repairOrderNumber: data.repairOrderNumber,
                                technicianFullName: account?.name,
                            },
                        ]}
                        outerTableClassName={classes.outerTable}
                        tableRowsDataSetter={setProcessedFailCodes}
                        tableHeaderRowRenderer={() => <ProcessBatteryUpdateRowDetailsHeader />}
                        tableBodyRowRenderer={(rowData) => {
                            return (
                                <TableRow key={rowData?.rowId} checked={rowData?.checked}>
                                    <TableCell className={classes.tableHeader}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography>Retailer</Typography>
                                            <Typography>{rowData?.retailer}</Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.tableHeader}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography>Repair Order #</Typography>
                                            <Typography>{rowData?.repairOrderNumber}</Typography>
                                        </div>
                                    </TableCell>
                                    <TableCell className={classes.tableHeader}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography>Technician Full Name</Typography>
                                            <Typography>{rowData.technicianFullName}</Typography>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        }}
                    />
                </div>
            </div>

            {/* New battery */}
            <div className="mb-2">
                <Typography variant="label">New battery pack</Typography>
            </div>
            <div className="row mb-2">
                <div className="col-12">
                    <TableRenderer
                        tableRowsData={[
                            {
                                rowId: '1',
                                ...newBinData,
                            },
                        ]}
                        outerTableClassName={classes.outerTable}
                        tableRowsDataSetter={setProcessedFailCodes}
                        tableHeaderRowRenderer={() =>
                            newBinData.format === 'Unknown' ? <BinInfoSingleHeader /> : <BinInfoHeader />
                        }
                        tableBodyRowRenderer={(rowData) => {
                            if (rowData.format === 'Unknown') {
                                return (
                                    <TableRow key={rowData.rowId} checked={rowData.checked}>
                                        <TableCell>{rowData.fullCode}</TableCell>
                                    </TableRow>
                                );
                            } else {
                                return (
                                    <TableRow key={rowData.rowId} checked={rowData.checked}>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                {rowData.partNumber && !editingFirstPartNumber ? (
                                                    <Typography>{rowData.partNumber}</Typography>
                                                ) : (
                                                    <>
                                                        <Form.Control
                                                            className={classes.fullWidthInput}
                                                            value={
                                                                (data.parsedNewBinData?.partNumber ??
                                                                    rowData.partNumber) ||
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                const hasSpecialChar = /[^A-Za-z0-9]/.test(value);
                                                                if (hasSpecialChar) {
                                                                    setInvalidPartNumber1(true);
                                                                    return;
                                                                }
                                                                setInvalidPartNumber1(false);
                                                                setEditingFirstPartNumber(true);
                                                                const val = e.target.value;
                                                                updateData({
                                                                    parsedNewBinData: {
                                                                        ...newBinData,
                                                                        ...data.parsedNewBinData,
                                                                        partNumber: val,
                                                                    },
                                                                });
                                                            }}
                                                            pattern="^[A-Za-z0-9]+$"
                                                            name="partNumberBin2"
                                                            type="text"
                                                            placeholder={t('Enter Part Number')}
                                                            maxLength={50}
                                                            required
                                                            isInvalid={invalidPartNumber1}
                                                        />
                                                        {/*<Form.Control.Feedback type="invalid">
                                                                Part Number must be alphanumeric.
                                                            </Form.Control.Feedback>*/}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography>{rowData.productionDate}</Typography>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography>{rowData.serialNumber}</Typography>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                        }}
                    />
                </div>
            </div>

            {/* Old battery */}
            <div className="mb-2">
                <Typography variant="label">Old battery pack</Typography>
            </div>
            <div className="row mb-2">
                <div className="col-12">
                    <TableRenderer
                        tableRowsData={[
                            {
                                rowId: '1',
                                ...oldBinData,
                            },
                        ]}
                        outerTableClassName={classes.outerTable}
                        tableRowsDataSetter={setProcessedFailCodes}
                        tableHeaderRowRenderer={() =>
                            oldBinData.format === 'Unknown' ? <BinInfoSingleHeader /> : <BinInfoHeader />
                        }
                        tableBodyRowRenderer={(rowData) => {
                            if (rowData.format === 'Unknown') {
                                return (
                                    <TableRow key={rowData.rowId} checked={rowData.checked}>
                                        <TableCell>{rowData.fullCode}</TableCell>
                                    </TableRow>
                                );
                            } else {
                                return (
                                    <TableRow key={rowData.rowId} checked={rowData.checked}>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                {rowData.partNumber && !editingSecondPartNumber ? (
                                                    <Typography>{rowData.partNumber}</Typography>
                                                ) : (
                                                    <>
                                                        <Form.Control
                                                            className={classes.fullWidthInput}
                                                            value={
                                                                (data.parsedOldBinData?.partNumber ??
                                                                    rowData.partNumber) ||
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                const hasSpecialChar = /[^A-Za-z0-9]/.test(value);
                                                                if (hasSpecialChar) {
                                                                    setInvalidPartNumber2(true);
                                                                    return;
                                                                }
                                                                setInvalidPartNumber2(false);
                                                                setEditingSecondPartNumber(true);
                                                                const val = e.target.value;
                                                                updateData({
                                                                    parsedOldBinData: {
                                                                        ...oldBinData,
                                                                        ...data.parsedOldBinData,
                                                                        partNumber: val,
                                                                    },
                                                                });
                                                            }}
                                                            pattern="^[A-Za-z0-9]+$"
                                                            name="partNumberBin1"
                                                            type="text"
                                                            placeholder={t('Enter Part Number')}
                                                            maxLength={50}
                                                            isInvalid={invalidPartNumber2}
                                                            required
                                                        />

                                                        {/*<Form.Control.Feedback type="invalid">
                                                        Part Number must be alphanumeric.
                                                    </Form.Control.Feedback>*/}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography>{rowData.productionDate}</Typography>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                <Typography>{rowData.serialNumber}</Typography>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            }
                        }}
                    />
                </div>
            </div>
        </Modal.Body>
    );
};

export default StepFiveContent;
